import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import StudyStayLogo from './StudyStayLogo';
import {
  ChevronDown,
  Info,
  Mail,
  Building2,
  Search,
  LogIn,
  UserPlus,
  Menu,
  X,
} from 'lucide-react';

const NAV_LINKS = [
  { to: '/about', label: 'Over ons', icon: Info },
  { to: '/contact', label: 'Contact', icon: Mail },
  { to: '/voor-verhuurders', label: 'Voor verhuurders', icon: Building2 },
  { to: '/welcome', label: 'Voor huurders', icon: Search },
];

export default function PublicNavbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setIsDropdownOpen(false);
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDropdownOpen]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/welcome" className="flex items-center gap-2.5 group">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center overflow-hidden shadow-sm border border-slate-200/60 group-hover:shadow-md transition-shadow duration-200"
              style={{ backgroundColor: '#60ADF0' }}
            >
              <StudyStayLogo className="w-6 h-6" />
            </div>
            <span className="text-xl font-bold text-slate-900">StudyStay</span>
          </Link>

          {/* Desktop: verhuurders link + dropdown + auth buttons */}
          <div className="hidden sm:flex items-center gap-3">
            {/* Standalone verhuurders link */}
            <Link
              to="/voor-verhuurders"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors duration-200"
            >
              <Building2 className="w-4 h-4 text-[#5EABEB]" />
              Voor verhuurders
            </Link>

            {/* Dropdown trigger */}
            <div ref={dropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 rounded-xl hover:bg-slate-50 transition-all duration-200"
              >
                Menu
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 rounded-2xl border border-slate-200 bg-white shadow-xl p-2 z-[80]">
                  {NAV_LINKS.map((link) => {
                    const Icon = link.icon;
                    const active = location.pathname === link.to;
                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          active
                            ? 'bg-[#5EABEB]/10 text-[#2B6FA6]'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${active ? 'text-[#2B6FA6]' : 'text-slate-400'}`} />
                        {link.label}
                      </Link>
                    );
                  })}

                  <div className="border-t border-slate-100 my-2" />

                  <Link
                    to="/user-type-login"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <LogIn className="w-4 h-4 text-slate-400" />
                    Inloggen
                  </Link>
                  <Link
                    to="/user-type-login?mode=register"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors"
                    style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
                  >
                    <UserPlus className="w-4 h-4" />
                    Registreer nu
                  </Link>
                </div>
              )}
            </div>

            {/* Always-visible auth buttons */}
            <Link
              to="/user-type-login"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors duration-200"
            >
              Inloggen
            </Link>
            <Link
              to="/user-type-login?mode=register"
              className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
            >
              Registreer nu
            </Link>
          </div>

          {/* Mobile: hamburger */}
          <button
            type="button"
            onClick={() => setIsMobileOpen((prev) => !prev)}
            className="sm:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors"
          >
            {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileOpen && (
        <div className="sm:hidden border-t border-slate-200/60 bg-white/95 backdrop-blur-xl">
          <div className="px-4 py-3 space-y-1">
            {NAV_LINKS.map((link) => {
              const Icon = link.icon;
              const active = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
                    active
                      ? 'bg-[#5EABEB]/10 text-[#2B6FA6]'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? 'text-[#2B6FA6]' : 'text-slate-400'}`} />
                  {link.label}
                </Link>
              );
            })}
            <div className="border-t border-slate-100 my-2" />
            <Link
              to="/user-type-login"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <LogIn className="w-5 h-5 text-slate-400" />
              Inloggen
            </Link>
            <Link
              to="/user-type-login?mode=register"
              className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-semibold text-white transition-colors"
              style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
            >
              <UserPlus className="w-5 h-5" />
              Registreer nu
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
